import i18n from "../../../i18n";
import React, { useState, useEffect } from "react";
import { useFormatter } from "../../../hooks/useFomatter";
import { useServerApi } from "../../../hooks/userServerApi";
import { IconSearch } from "@tabler/icons";
import {
  Button,
  Text,
  Title,
  Badge,
  Accordion,
  LoadingOverlay,
  Box,
  Paper,
  Table,
  Group,
  Stack,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import moment from "moment";
import EntityTabAnchor from "./EntityTabAnchor";
import DuplicateBidConflictsAlert from "./DuplicateBidConflictsAlert";
import {
  downloadExcel,
  matchesQuery,
  payrollFileName,
  ResultTools,
} from "./listExport";

const PAGE_SIZE = 50;

const ListPager = ({ page, total, onChange }) => {
  const pages = Math.ceil(total / PAGE_SIZE) || 1;
  if (total <= PAGE_SIZE) return null;
  return (
    <Group position="apart" mt="md">
      <Text size="xs" color="dimmed">
        Page {page} of {pages} ({total})
      </Text>
      <Group spacing="xs">
        <Button
          size="xs"
          variant="light"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          {i18n.t("Previous")}
        </Button>
        <Button
          size="xs"
          variant="light"
          disabled={page >= pages}
          onClick={() => onChange(page + 1)}
        >
          {i18n.t("Next")}
        </Button>
      </Group>
    </Group>
  );
};

const ClientPaymentsTab = ({ form }) => {
  const [api] = useServerApi();
  const [formatter] = useFormatter();

  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [payments, setPayments] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [page, setPage] = useState(1);
  const [openPaymentId, setOpenPaymentId] = useState(null);
  const [query, setQuery] = useState("");

  const payrollStatus = form?.values?.status;
  const isConfirmed =
    payrollStatus === "CONFIRMED" || payrollStatus === "COMPLETED";

  const loadSkippedDuplicateConflicts = async () => {
    if (!form?.values?._id) {
      setConflicts([]);
      return;
    }
    try {
      const result = await api.Payroll2.getDuplicateBidConflicts(
        form.values._id,
      );
      if (result?.success) {
        setConflicts(
          result.data?.excludedConflicts ||
            result.data?.skippedDuplicateSources ||
            result.data?.conflicts ||
            [],
        );
      }
    } catch (error) {
      console.log("loadSkippedDuplicateConflicts error:", error);
    }
  };

  useEffect(() => {
    loadSkippedDuplicateConflicts();
  }, [form?.values?._id]);

  useEffect(() => {
    const loadSavedClientPayments = async () => {
      const savedSearch = form?.values?.clientPaymentSearch;
      if (savedSearch) {
        if (savedSearch.fromDate) setFromDate(new Date(savedSearch.fromDate));
        if (savedSearch.toDate) setToDate(new Date(savedSearch.toDate));
      }

      const savedClientPaymentIds = form?.values?.clientPayments;
      if (!savedClientPaymentIds || savedClientPaymentIds.length === 0) return;

      try {
        setLoading(true);
        const result = await api.Payroll2.getClientPaymentsByIds(
          savedClientPaymentIds,
        );
        if (result?.success && result.data?.payments) {
          setPayments(result.data.payments);
          setPage(1);
          setOpenPaymentId(null);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    loadSavedClientPayments();
  }, [form?.values?._id]);

  const saveClientPaymentsToPayroll = async (paymentIds) => {
    if (!form?.values?._id || !form?.updateAndSave) return;
    try {
      await form.updateAndSave({
        clientPayments: paymentIds,
        clientPaymentSearch: {
          fromDate,
          toDate,
        },
      });
    } catch (error) {
      console.log("Error saving client payments to payroll:", error);
    }
  };

  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      return;
    }

    try {
      setLoading(true);
      const result = await api.Payroll2.getClientPaymentsForPayroll(
        moment(fromDate).format("YYYY-MM-DD"),
        moment(toDate).format("YYYY-MM-DD"),
        form?.values?._id,
      );

      if (result && result.success && result.data && result.data.payments) {
        setPayments(result.data.payments);
        setPage(1);
        setOpenPaymentId(null);
        setConflicts(result.data.excludedConflicts || result.data.conflicts || []);
        const paymentIds = result.data.payments.map((p) => p._id);
        await saveClientPaymentsToPayroll(paymentIds);
        console.log(
          `Loaded ${result.data.count} client payments (no pagination limit)`,
        );
      } else {
        setPayments([]);
        setPage(1);
        setOpenPaymentId(null);
        setConflicts([]);
      }
    } catch (error) {
      console.log("Error fetching client payments:", error);
      setPayments([]);
      setPage(1);
      setOpenPaymentId(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "MATCHED":
        return "green";
      case "MATCHING":
        return "yellow";
      case "VOID":
        return "red";
      case "REFUND":
        return "orange";
      case "DIRECT":
        return "blue";
      case "PENDING_APPROVAL":
        return "orange";
      default:
        return "gray";
    }
  };

  const filteredPayments = payments.filter((payment) =>
    matchesQuery(query, [
      payment.code,
      payment.status,
      payment.amount,
      payment.paymentMethod,
      payment.chequeNumber,
      payment.remark,
      payment.bankAccount?.accountNumber,
      payment.fromClient?.code,
      payment.fromClient?.name,
      ...(payment.debitNotes || []).flatMap((dn) => [
        dn.code,
        dn.client?.code,
        dn.client?.name,
      ]),
      ...(payment.debitNoteAssignments || []).flatMap((assignment) => [
        assignment.debitNote?.code,
        assignment.debitNote?.client?.code,
        assignment.debitNote?.client?.name,
        assignment.debitNote?.productType?.name,
        assignment.paymentAmount,
      ]),
    ]),
  );
  const pageCount = Math.max(1, Math.ceil(filteredPayments.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagePayments = filteredPayments.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const downloadPayments = () => {
    const rows = [];
    payments.forEach((payment) => {
      const summary = [
        payment.code || "",
        payment.date ? moment(payment.date).format("YYYY-MM-DD") : "",
        payment.matchedDate
          ? moment(payment.matchedDate).format("YYYY-MM-DD")
          : "",
        payment.status || "",
        Number(payment.amount) || 0,
        payment.paymentMethod || "",
        payment.bankAccount?.accountNumber || "",
        payment.chequeNumber || "",
        payment.remark || "",
      ];
      const assignments = payment.debitNoteAssignments || [];
      if (!assignments.length) {
        rows.push([...summary, "", "", "", "", "", ""]);
        return;
      }
      assignments.forEach((assignment) => {
        const period = assignment.debitNote?.period;
        rows.push([
          ...summary,
          assignment.debitNote?.code || "",
          assignment.debitNote?.productType?.name || "",
          period?.start ? moment(period.start).format("YYYY-MM-DD") : "",
          period?.end ? moment(period.end).format("YYYY-MM-DD") : "",
          assignment.debitNote?.client?.code ||
            payment.fromClient?.code ||
            "",
          assignment.debitNote?.client?.name ||
            payment.fromClient?.name ||
            "",
          Number(assignment.paymentAmount) || 0,
        ]);
      });
    });
    downloadExcel(
      payrollFileName("client-payments", form?.values?.code),
      [
        "Payment Code",
        "Payment Date",
        "Matched Date",
        "Status",
        "Amount",
        "Payment Method",
        "Bank Account",
        "Cheque Number",
        "Remark",
        "Debit Note",
        "Class",
        "Period Start",
        "Period End",
        "Client Code",
        "Client Name",
        "Assignment Amount",
      ],
      rows,
    );
  };

  const showPage = (nextPage) => {
    setPage(nextPage);
    setOpenPaymentId(null);
  };

  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} overlayBlur={2} />

      <Paper p="md" withBorder mb="lg">
        <Title order={5} mb="md">
          Search Client Payments
        </Title>

        <Group align="flex-end" spacing="md">
          <DatePicker
            label="From Date"
            size="xs"
            placeholder="Select start date"
            value={fromDate}
            onChange={setFromDate}
            allowFreeInput
            clearable
            disabled={isConfirmed}
          />

          <DatePicker
            label="To Date"
            size="xs"
            placeholder="Select end date"
            value={toDate}
            onChange={setToDate}
            allowFreeInput
            clearable
            disabled={isConfirmed}
          />

          <Button
            size="xs"
            leftIcon={<IconSearch size={14} />}
            onClick={handleSearch}
            disabled={!fromDate || !toDate || isConfirmed}
          >{i18n.t("Search")}</Button>
        </Group>

        <Text size="xs" color="dimmed" mt="xs">
          Only the client payments matched within the dates will be filtered
        </Text>

        {isConfirmed && (
          <Text size="xs" color="orange" mt="xs">
            Payroll is completed. No more edits allowed.
          </Text>
        )}
      </Paper>

      <Box mb="md">
        <DuplicateBidConflictsAlert conflicts={conflicts} />
      </Box>

      {payments.length > 0 && (
        <Paper p="md" withBorder>
          <Group position="apart" mb="md">
            <Title order={5}>{i18n.t("Results")}</Title>
            <Group spacing="sm">
              <ResultTools
                query={query}
                onQueryChange={(value) => {
                  setQuery(value);
                  setPage(1);
                  setOpenPaymentId(null);
                }}
                onDownload={downloadPayments}
                placeholder={i18n.t("Code, client, debit note")}
              />
              <Badge size="lg">
                {query.trim()
                  ? `${filteredPayments.length} / ${payments.length}`
                  : `${payments.length} payments found`}
              </Badge>
            </Group>
          </Group>

          {filteredPayments.length === 0 ? (
            <Text color="dimmed" align="center">
              No payments match this search.
            </Text>
          ) : (
          <Accordion
            variant="separated"
            value={openPaymentId}
            onChange={setOpenPaymentId}
          >
            {pagePayments.map((payment, index) => (
              <Accordion.Item key={payment._id} value={payment._id}>
                <Accordion.Control>
                  <Group position="apart" noWrap>
                    <Group spacing="xl" noWrap>
                      <Badge
                        size="sm"
                        variant="filled"
                        color="dark"
                        sx={{ minWidth: 36, flexShrink: 0 }}
                      >
                        {(currentPage - 1) * PAGE_SIZE + index + 1}
                      </Badge>

                      <Box w={110}>
                        <Text size="sm" weight={600}>
                          {payment.code}
                        </Text>
                        <Text size="xs" color="dimmed">
                          {payment.date
                            ? moment(payment.date).format("YYYY-MM-DD")
                            : "-"}
                        </Text>
                      </Box>

                      <Box w={550}>
                        <Text size="sm" weight={500} lineClamp={1}>
                          {payment.debitNotes && payment.debitNotes.length > 0
                            ? payment.debitNotes
                                .map((dn) => dn.code || dn)
                                .join(" / ")
                            : "-"}
                        </Text>
                        <Text size="xs" color="dimmed" lineClamp={1}>
                          {payment.debitNotes?.[0]?.client?.code ||
                            payment.fromClient?.code ||
                            "-"}{" "}
                          -{" "}
                          {payment.debitNotes?.[0]?.client?.name ||
                            payment.fromClient?.name ||
                            "-"}
                        </Text>
                      </Box>

                      <Box w={120}>
                        <Text size="sm" weight={500}>
                          {formatter.currency(payment.amount)}
                        </Text>
                      </Box>

                      <Box w={100}>
                        <Text size="sm">
                          {payment.matchedDate
                            ? moment(payment.matchedDate).format("YYYY-MM-DD")
                            : "-"}
                        </Text>
                        <Text size="xs" color="dimmed">{i18n.t("Matched Date")}</Text>
                      </Box>
                    </Group>

                    <Badge color={getStatusColor(payment.status)} size="sm">
                      {payment.status}
                    </Badge>
                  </Group>
                </Accordion.Control>

                <Accordion.Panel>
                  {openPaymentId === payment._id && (
                  <Stack spacing="md" mt="md">
                    <Group spacing="xl">
                      <Box>
                        <Text size="xs" color="dimmed">
                          Payment Code
                        </Text>
                        <Text size="sm" weight={500}>
                          {payment.code}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">{i18n.t("Payment Date")}</Text>
                        <Text size="sm">
                          {payment.date
                            ? moment(payment.date).format("YYYY-MM-DD")
                            : "-"}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">{i18n.t("Matched Date")}</Text>
                        <Text size="sm">
                          {payment.matchedDate
                            ? moment(payment.matchedDate).format("YYYY-MM-DD")
                            : "-"}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">
                          Created Date
                        </Text>
                        <Text size="sm">
                          {payment.createdAt
                            ? moment(payment.createdAt).format("YYYY-MM-DD")
                            : "-"}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">{i18n.t("Amount")}</Text>
                        <Text size="sm" weight={500}>
                          {formatter.currency(payment.amount)}
                        </Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">{i18n.t("Payment Method")}</Text>
                        <Text size="sm">{payment.paymentMethod || "-"}</Text>
                      </Box>
                      <Box>
                        <Text size="xs" color="dimmed">{i18n.t("Status")}</Text>
                        <Badge color={getStatusColor(payment.status)} size="sm">
                          {payment.status}
                        </Badge>
                      </Box>
                    </Group>

                    <Group spacing="xl">
                      <Box>
                        <Text size="xs" color="dimmed">{i18n.t("Bank Account")}</Text>
                        <Text size="sm">
                          {payment.bankAccount?.accountNumber || "-"}
                        </Text>
                      </Box>
                      {payment.chequeNumber && (
                        <Box>
                          <Text size="xs" color="dimmed">{i18n.t("Cheque Number")}</Text>
                          <Text size="sm">{payment.chequeNumber}</Text>
                        </Box>
                      )}
                      {payment.remark && (
                        <Box>
                          <Text size="xs" color="dimmed">{i18n.t("Remark")}</Text>
                          <Text size="sm">{payment.remark}</Text>
                        </Box>
                      )}
                    </Group>

                    {payment.debitNoteAssignments &&
                      payment.debitNoteAssignments.length > 0 && (
                        <>
                          <Text size="sm" weight={500}>
                            Debit Note Assignments:
                          </Text>
                          <Table fontSize="xs" striped highlightOnHover>
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>{i18n.t("DN Code")}</th>
                                <th>{i18n.t("Class")}</th>
                                <th>{i18n.t("DN Period")}</th>
                                <th>{i18n.t("Client")}</th>
                                <th>{i18n.t("Payment Amount")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payment.debitNoteAssignments.map(
                                (assignment, idx) => (
                                  <tr key={idx}>
                                    <td>{idx + 1}</td>
                                    <td>
                                      <EntityTabAnchor
                                        entity="debitNote"
                                        id={
                                          assignment.debitNote?._id ||
                                          assignment.debitNote
                                        }
                                        label={
                                          assignment.debitNote?.code ||
                                          assignment.debitNote
                                        }
                                      />
                                    </td>
                                    <td>
                                      {assignment.debitNote?.productType
                                        ?.name || "-"}
                                    </td>
                                    <td>
                                      {assignment.debitNote?.period?.start
                                        ? `${moment(
                                            assignment.debitNote.period.start,
                                          ).format("YYYY-MM-DD")} ~ ${
                                            assignment.debitNote.period?.end
                                              ? moment(
                                                  assignment.debitNote.period
                                                    .end,
                                                ).format("YYYY-MM-DD")
                                              : "-"
                                          }`
                                        : "-"}
                                    </td>
                                    <td>
                                      {assignment.debitNote?.client?.code ||
                                        "-"}{" "}
                                      {assignment.debitNote?.client?.name &&
                                        `- ${assignment.debitNote.client.name}`}
                                    </td>
                                    <td>
                                      {formatter.currency(
                                        assignment.paymentAmount,
                                      )}
                                    </td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </Table>
                        </>
                      )}
                  </Stack>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
          )}
          <ListPager
            page={currentPage}
            total={filteredPayments.length}
            onChange={showPage}
          />
        </Paper>
      )}

      {payments.length === 0 && !loading && (
        <Paper p="xl" withBorder>
          <Text color="dimmed" align="center">
            No payments found. Please enter date range and click Search.
          </Text>
        </Paper>
      )}
    </Box>
  );
};

export default ClientPaymentsTab;
